import i18n from 'src/locales/i18n'

export type DateInput = string | number | Date | null | undefined

export type DateTimeVariant = 'date' | 'dateTime' | 'time' | 'relative' | 'auto'

type Style = 'short' | 'medium' | 'long' | 'full'

type FormatOptions = {
  /** Override the active i18n language. */
  locale?: string
  /** Date portion granularity. Defaults to 'medium' ("Mar 15, 2026"). */
  dateStyle?: Style
  /** Time portion granularity. Defaults to 'short' ("14:32") when used. */
  timeStyle?: Style
  /** Override timezone (otherwise the browser's local zone). */
  timeZone?: string
}

const RELATIVE_THRESHOLDS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['week', 60 * 60 * 24 * 7],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1]
]

const AUTO_RELATIVE_WINDOW_DAYS = 7

const resolveLocale = (override?: string) => override ?? i18n.language ?? 'en'

const toDate = (value: DateInput): Date | null => {
  if (value == null) return null
  const d = value instanceof Date ? value : new Date(value)

  return Number.isNaN(d.getTime()) ? null : d
}

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>()
const relativeFormatterCache = new Map<string, Intl.RelativeTimeFormat>()

const getDateFormatter = (
  locale: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(options)}`
  let f = dateFormatterCache.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options)
    dateFormatterCache.set(key, f)
  }
  return f
}

const getRelativeFormatter = (locale: string): Intl.RelativeTimeFormat => {
  let f = relativeFormatterCache.get(locale)
  if (!f) {
    f = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    relativeFormatterCache.set(locale, f)
  }
  return f
}

/** Format the date portion only (e.g. "Mar 15, 2026"). Returns empty string for null/invalid. */
export const formatDate = (
  value: DateInput,
  { locale, dateStyle = 'medium', timeZone }: FormatOptions = {}
): string => {
  const date = toDate(value)
  if (!date) return ''
  return getDateFormatter(resolveLocale(locale), {
    dateStyle,
    timeZone
  }).format(date)
}

/** Format date + time (e.g. "Mar 15, 2026, 2:30 PM"). */
export const formatDateTime = (
  value: DateInput,
  {
    locale,
    dateStyle = 'medium',
    timeStyle = 'short',
    timeZone
  }: FormatOptions = {}
): string => {
  const date = toDate(value)
  if (!date) return ''
  return getDateFormatter(resolveLocale(locale), {
    dateStyle,
    timeStyle,
    timeZone
  }).format(date)
}

/** Format the time portion only (e.g. "2:30 PM"). */
export const formatTime = (
  value: DateInput,
  { locale, timeStyle = 'short', timeZone }: FormatOptions = {}
): string => {
  const date = toDate(value)
  if (!date) return ''
  return getDateFormatter(resolveLocale(locale), {
    timeStyle,
    timeZone
  }).format(date)
}

/** Localized relative time (e.g. "2 hours ago", "in 3 days"). */
export const formatRelative = (
  value: DateInput,
  { locale }: Pick<FormatOptions, 'locale'> = {}
): string => {
  const date = toDate(value)
  if (!date) return ''
  const formatter = getRelativeFormatter(resolveLocale(locale))
  const diffSeconds = (date.getTime() - Date.now()) / 1000
  const absSeconds = Math.abs(diffSeconds)
  for (const [unit, secondsPerUnit] of RELATIVE_THRESHOLDS) {
    if (absSeconds >= secondsPerUnit || unit === 'second') {
      return formatter.format(Math.round(diffSeconds / secondsPerUnit), unit)
    }
  }
  return formatter.format(0, 'second')
}

/** Pick the right format for the situation:
 *  - within the last 7 days → relative ("yesterday", "3 hours ago")
 *  - older or future → medium date ("Mar 15, 2026")
 */
export const formatAuto = (
  value: DateInput,
  options: FormatOptions = {}
): string => {
  const date = toDate(value)
  if (!date) return ''
  const ageMs = Math.abs(date.getTime() - Date.now())
  const withinWindow = ageMs < AUTO_RELATIVE_WINDOW_DAYS * 86_400_000

  return withinWindow
    ? formatRelative(date, options)
    : formatDate(date, options)
}

/** Dispatch helper for `<DateTimeCell variant={...} />`. */
export const formatByVariant = (
  value: DateInput,
  variant: DateTimeVariant,
  options: FormatOptions = {}
): string => {
  switch (variant) {
    case 'date':
      return formatDate(value, options)
    case 'dateTime':
      return formatDateTime(value, options)
    case 'time':
      return formatTime(value, options)
    case 'relative':
      return formatRelative(value, options)
    case 'auto':
      return formatAuto(value, options)
  }
}

/** ISO string used for the tooltip / `<time dateTime>` attribute. */
export const toIsoString = (value: DateInput): string => {
  const date = toDate(value)

  return date ? date.toISOString() : ''
}

export type GreetingKey = 'morning' | 'afternoon' | 'evening' | 'late'

/**
 * Time-of-day bucket used to pick a localized greeting ("Good morning", …).
 * Centralized so the bucket boundaries stay consistent across pages.
 */
export const greetingKey = (now: Date = new Date()): GreetingKey => {
  const hour = now.getHours()
  if (hour < 5) return 'late'
  if (hour < 12) return 'morning'
  if (hour < 18) return 'afternoon'

  return 'evening'
}
